import React, { useState } from "react";
import { Settings as SlickSettings } from "react-slick";
import { BREAKPOINTS } from "web/react/constants";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import FullscreenGalleryEmo from "./__LEGACY__/product-image-gallery-fullscreen-emo";
import { Image } from "./product-gallery-image";
import { ProductImageGalleryCarouselEmo } from "./product-image-carousel-emo";
import { ProductImageGalleryFullscreen } from "./product-image-gallery-fullscreen";

type ProductImageGalleryProps = {
    images: Image[];
    currentSlideIndex?: number;
    productId: string;
    isInStockProductPage?: boolean;
};

export function ProductImageGallery({
    images,
    productId,
    isInStockProductPage = false,
}: ProductImageGalleryProps): React.ReactElement {
    const [fullscreen, setFullscreen] = useState(false);
    const [initialSlideIndex, setInitialSlideIndex] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [seenAll, setSeenAll] = useState(false);

    const isGalleryRedesignEnabled = !!(
        isInStockProductPage && environment.getFeature("beam_web_pdp_gallery_fullscreen_redesign")
    );

    function handleImageClick(event, index): void {
        setInitialSlideIndex(index);
        setCurrentSlideIndex(index);
        setFullscreen(true);
    }

    function onSlideChange(curSlide, nextSlide): void {
        setCurrentSlideIndex(nextSlide);
        analytics.event("product_gallery", "engagement", "changed_image", false, {
            product_id: productId,
        });

        if (!seenAll) {
            const lastSlideIndex = images.length - 1;

            if (nextSlide === lastSlideIndex) {
                setSeenAll(true);
                analytics.event("product_gallery", "engagement", "seen_all", false, {
                    product_id: productId,
                });
            }
        }
    }

    const afterSlideChange = (currentSlide: number): void => {
        onSlideChange(undefined, currentSlide);
    };

    // Slick settings and props for the fullscreen gallery
    const fullscreenGalleryEmoSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: initialSlideIndex,
        beforeChange: onSlideChange,
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        lazyLoad: undefined,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    const InPageViewSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: currentSlideIndex,
        afterChange: afterSlideChange,
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        lazyLoad: undefined,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    slidesToShow: 1,
                    speed: 200, // we need this for smooth swipe
                    touchMove: true,
                    draggable: false,
                },
            },
            {
                breakpoint: BREAKPOINTS.Large,
                settings: {
                    slidesToShow: 1,
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    const fullscreenGalleryEmoProps = {
        isOpen: fullscreen,
        onClose: (): void => {
            setFullscreen(false);
            analytics.event("product_gallery", "engagement", "close_fullscreen", false, {
                product_id: productId,
            });
        },
        images: images,
        settings: fullscreenGalleryEmoSettings,
    };

    return (
        <>
            {fullscreen && isGalleryRedesignEnabled ? (
                <ProductImageGalleryFullscreen
                    images={images}
                    initialSlideIndex={initialSlideIndex}
                    onClose={(): void => {
                        setFullscreen(false);
                    }}
                />
            ) : fullscreen ? (
                <FullscreenGalleryEmo
                    currentSlideIndex={currentSlideIndex}
                    productId={productId}
                    {...fullscreenGalleryEmoProps}
                />
            ) : (
                <ProductImageGalleryCarouselEmo
                    images={images}
                    settings={InPageViewSettings}
                    isFullscreen={fullscreen}
                    handleImageClick={(event, index): void => handleImageClick(event, index)}
                    onClose={(): void => setFullscreen(false)}
                    currentSlideIndex={currentSlideIndex}
                    productId={productId}
                />
            )}
        </>
    );
}
